import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import TitleWithBack from '../../components/common/TitleWithBack';
import { useDialog } from '../../context/DialogContext';
import { isSeriesActive } from '../../utils/helpers';

// Enrolment is no longer a manual step here - candidates fetched via the API
// Gateway sync arrive already enrolled (program/year/semester/exam centre
// set, status active) for whatever year+semester their paper belongs to
// (see ApiGatewaySyncService.upsertCandidate on the backend), and manually
// created/bulk-uploaded students already require year+semester at creation
// time. Correcting an individual student's program/year/semester/status is
// still available via the Edit Student page.
export default function StudentProfile() {
  const { id } = useParams();
  const { alert } = useDialog();
  const [student, setStudent] = useState(null);
  const [work, setWork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modules, setModules] = useState([]);
  const [exams, setExams] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`/api/students/${id}`),
      axios.get('/api/archiver/reports/students-work'),
      axios.get('/api/modules'),
      axios.get('/api/exams'),
    ])
      .then(([stuRes, workRes, modulesRes, examsRes]) => {
        setStudent(stuRes.data);
        setWork((workRes.data || []).filter(w => w.studentId === Number(id)));
        setModules(Array.isArray(modulesRes.data) ? modulesRes.data : (modulesRes.data.data || []));
        setExams(Array.isArray(examsRes.data) ? examsRes.data : (examsRes.data.data || []));
        setLoading(false);
      })
      .catch(() => {
        setError('Error loading student profile.');
        setLoading(false);
      });
  }, [id]);

  const groupWorkBySemester = () => {
    const grouped = {};
    work.forEach(w => {
      const key = `${w.year || 'Unknown'} - ${w.semester || 'Unknown'}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(w);
    });
    return grouped;
  };

  // Only modules whose exam belongs to a currently active series are offered
  // here - a module scheduled under a series that hasn't started yet or has
  // already ended isn't something the candidate can actually sit for right
  // now.

  // Availability is purely program/year/semester enrolment - it must not
  // depend on whether an examiner has already been assigned to grade the
  // exam (ExamAssignment is a separate, later admin step that most
  // newly-enrolled candidates won't have yet, which is why this used to show
  // nothing). Exam has no exam-centre concept at all - centre is only ever
  // an attribute of the student, never something to match against here.
  const availableModules = Array.from(
    new Map(
      exams
        .map(exam => {
          const module = exam.module && modules.find(m => m.id === exam.module.id);
          if (
            module &&
            student &&
            exam.status !== 'CANCELLED' &&
            isSeriesActive(exam.examSeries) &&
            module.program?.id === student.program?.id &&
            (module.yearOfStudy?.id ?? null) === (student.yearOfStudy?.id ?? null) &&
            (module.semester?.id ?? null) === (student.semester?.id ?? null)
          ) {
            return [module.id, { module, exam }];
          }
          return null;
        })
        .filter(Boolean)
    ).values()
  );

  if (loading) return <div className="container mt-4">Loading...</div>;
  if (error) return <div className="container mt-4 text-danger">{error}</div>;

  const groupedWork = groupWorkBySemester();

  const handleDownload = async (documentId, documentName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/archiver/reports/students-work/download/${documentId}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentName || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download file.', { title: 'Error', variant: 'danger' });
    }
  };

  return (
    <div className="container mt-2">
      <TitleWithBack title={`Student Profile: ${student.name}`} />
      <div className="card mb-4">
        <div className="card-body">
          <h5>Available Modules to Sit For</h5>
          {availableModules.length === 0 ? (
            <div>Nothing to show here</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Module Name</th>
                    <th>Module Code</th>
                    <th>Exam Type</th>
                    <th>Exam Date</th>
                  </tr>
                </thead>
                <tbody>
                  {availableModules.map(({ module, exam }) => (
                    <tr key={module.id}>
                      <td>{module.moduleName}</td>
                      <td>{module.moduleCode}</td>
                      <td>{module.examType?.name || '-'}</td>
                      <td>{exam.examDate ? new Date(exam.examDate).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <h5>Exam Work by Semester</h5>
          {Object.keys(groupedWork).length === 0 && <div>Nothing to show here</div>}
          {Object.entries(groupedWork).map(([sem, works]) => (
            <div key={sem} className="mb-3">
              <h6>{sem}</h6>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Module</th>
                      <th>Document Name</th>
                      <th>Exam Series</th>
                      <th>Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {works.map(w => (
                      <tr key={w.documentId}>
                        <td>{w.module || w.moduleCode}</td>
                        <td>{w.documentName}</td>
                        <td>{w.examSeries || '-'}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleDownload(w.documentId, w.documentName)}
                          >
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
